import React, { FC, useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  FormControlLabel,
  IconButton,
  Switch,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import AddIcon from "@mui/icons-material/Add";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import {
  useArchivePatientMutation,
  useGetPatientsQuery,
  useUnarchivePatientMutation,
  useUpdatePatientStatusMutation,
} from "@store/patientApi";
import { Patient } from "@models/patient";
import Loader from "@components/Loader/Loader";
import { useDispatch } from "react-redux";
import { Mode, selectPatientId, setCompanionMode } from "@store/homeSlice";
import { ADD_PATIENT_FLOW } from "@components/Onboarding/onboardingFlow";
import { setMessage } from "@store/snackbarSlice";
import PageWrapper from "@components/Home/PageWrapper";
import HomePageTitle from "@components/Home/HomePageTitle/HomePageTitle";
import PatientList from "@components/PatientList";
import {
  PatientDrawer,
  PatientExportDialog,
  PatientExportKind,
} from "@components/Home/ManagePatients";
import EmptyState from "@components/EmptyState/EmptyState";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";

const ManagePatientsPage: FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const params = useParams();
  const dispatch = useDispatch();
  const [showArchived, setShowArchived] = useState(true);
  const [selectedPatient, setSelectedPatient] = useState<Patient | undefined>();
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [exportKind, setExportKind] = useState<PatientExportKind | null>(null);
  const { data, isLoading, isError } = useGetPatientsQuery({
    includeArchived: true,
    includeLastActivity: true,
  });
  const [updateStatus, { isLoading: isUpdatingStatus }] =
    useUpdatePatientStatusMutation();
  const [archivePatient, { isLoading: isArchiving }] =
    useArchivePatientMutation();
  const [unarchivePatient, { isLoading: isUnarchiving }] =
    useUnarchivePatientMutation();

  const patients = useMemo(() => {
    const list = data?.data ?? [];
    if (showArchived) {
      return list;
    }
    return list.filter((p) => !p.archivedAt);
  }, [data, showArchived]);

  const isMutating = isUpdatingStatus || isArchiving || isUnarchiving;

  useEffect(() => {
    const id = params.id;
    if (!id || !data?.data) {
      return;
    }
    const patient = data.data.find((p) => p.id === id);
    if (patient) {
      setSelectedPatient(patient);
    }
  }, [params.id, data]);

  const onEdit = useCallback(
    (patient: Patient) => {
      setSelectedPatient(patient);
      navigate(`/patients/manage/${patient.id}`);
    },
    [navigate],
  );

  const onClose = useCallback(() => {
    setSelectedPatient(undefined);
    navigate("/patients/manage");
  }, [navigate]);

  const openPortal = useCallback(
    (patient: Patient) => {
      if (!patient.isActive) {
        dispatch(
          setMessage({
            content: t("patientManage.portalInactive"),
            severity: "warning",
          }),
        );
        return;
      }
      dispatch(selectPatientId(patient.id));
      dispatch(setCompanionMode(Mode.Patient));
      navigate("/portal");
    },
    [dispatch, navigate, t],
  );

  const handleToggleActive = useCallback(
    async (patient: Patient) => {
      try {
        const updated = await updateStatus({
          id: patient.id,
          isActive: !patient.isActive,
        }).unwrap();
        setSelectedPatient((current) =>
          current?.id === patient.id ? updated : current,
        );
      } catch {
        dispatch(
          setMessage({
            content: t("patientManage.actionError"),
            severity: "error",
          }),
        );
      }
    },
    [dispatch, t, updateStatus],
  );

  const handleArchiveToggle = useCallback(
    async (patient: Patient) => {
      try {
        const updated = patient.archivedAt
          ? await unarchivePatient(patient.id).unwrap()
          : await archivePatient(patient.id).unwrap();
        setSelectedPatient((current) =>
          current?.id === patient.id ? updated : current,
        );
      } catch {
        dispatch(
          setMessage({
            content: t("patientManage.actionError"),
            severity: "error",
          }),
        );
      }
    },
    [archivePatient, dispatch, t, unarchivePatient],
  );

  const handleEditProfile = useCallback(
    (patient: Patient) => {
      dispatch(selectPatientId(patient.id));
      navigate("/patient-info");
    },
    [dispatch, navigate],
  );

  const handleSelectionChange = useCallback(
    (patientId: string, selected: boolean) => {
      setSelectedIds((prev) => {
        const next = new Set(prev);
        if (selected) {
          next.add(patientId);
        } else {
          next.delete(patientId);
        }
        return next;
      });
    },
    [],
  );

  const handleSelectAll = useCallback(
    (selected: boolean) => {
      if (selected) {
        setSelectedIds(new Set(patients.map((p) => p.id)));
      } else {
        setSelectedIds(new Set());
      }
    },
    [patients],
  );

  const selectedPatientIds = useMemo(
    () => Array.from(selectedIds),
    [selectedIds],
  );

  const exportDisabled = selectedIds.size === 0;

  return (
    <PageWrapper>
      <Box display="flex" alignItems="center" gap={1} marginBottom={2}>
        <IconButton
          aria-label={t("patientManage.backToDashboard")}
          onClick={() => navigate("/")}
          sx={{ color: "#171A1FFF" }}
        >
          <ArrowBackIcon />
        </IconButton>
      </Box>

      <HomePageTitle title={t("patientManage.title")} />

      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        flexWrap="wrap"
        gap={2}
        marginBottom={3}
      >
        <FormControlLabel
          control={
            <Switch
              checked={showArchived}
              onChange={(_, checked) => setShowArchived(checked)}
            />
          }
          label={t("patientManage.showArchived")}
        />
        <Box display="flex" gap={1} flexWrap="wrap">
          <Button
            variant="outlined"
            startIcon={<FileDownloadOutlinedIcon />}
            disabled={exportDisabled}
            onClick={() => setExportKind("games")}
          >
            {t("patientManage.export.gamesButton")}
          </Button>
          <Button
            variant="outlined"
            startIcon={<FileDownloadOutlinedIcon />}
            disabled={exportDisabled}
            onClick={() => setExportKind("conversations")}
          >
            {t("patientManage.export.conversationsButton")}
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() =>
              navigate(`/onboarding/profile?flow=${ADD_PATIENT_FLOW}`)
            }
          >
            {t("patientManage.addPatient")}
          </Button>
        </Box>
      </Box>

      {isLoading ? (
        <Loader />
      ) : isError ? (
        <EmptyState
          image={`${process.env.PUBLIC_URL}/images/art-transcript.jpeg`}
          label={t("patientManage.loadError")}
        />
      ) : patients.length ? (
        <PatientList
          patients={patients}
          selectedIds={selectedIds}
          onSelectionChange={handleSelectionChange}
          onSelectAll={handleSelectAll}
          onEdit={onEdit}
          onToggleActive={handleToggleActive}
          onArchiveToggle={handleArchiveToggle}
          onOpenPortal={openPortal}
          onEditProfile={handleEditProfile}
          isMutating={isMutating}
        />
      ) : (
        <EmptyState
          image={`${process.env.PUBLIC_URL}/images/art-transcript.jpeg`}
          label={t("patientManage.empty")}
        >
          <Box display="flex" justifyContent="center" marginTop={2}>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() =>
                navigate(`/onboarding/profile?flow=${ADD_PATIENT_FLOW}`)
              }
            >
              {t("patientManage.addPatient")}
            </Button>
          </Box>
        </EmptyState>
      )}

      <PatientDrawer
        patient={selectedPatient}
        onClose={onClose}
        onToggleActive={handleToggleActive}
        onArchiveToggle={handleArchiveToggle}
        onOpenPortal={openPortal}
        onEditProfile={handleEditProfile}
        isMutating={isMutating}
      />

      {exportKind && (
        <PatientExportDialog
          open
          kind={exportKind}
          patientIds={selectedPatientIds}
          onClose={() => setExportKind(null)}
          onError={(message) =>
            dispatch(
              setMessage({
                content: message,
                severity: "error",
              }),
            )
          }
        />
      )}
    </PageWrapper>
  );
};

export default ManagePatientsPage;
